/**
 * CounterCool Section Nav.
 *
 * Puts `aria-current` on the link whose section the shopper is reading, so
 * assistive software announces the right item. Everything visible is CSS.
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Attributes/aria-current
 *
 * The reading position comes from an IntersectionObserver, which reports only
 * the frames where a section crossed the band.
 * https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 */
(function () {
  // The -60% bottom margin shrinks the watched area to the top 40% of the
  // viewport, so a section counts as current once it reaches that band.
  const OBSERVED_BAND = '0px 0px -60% 0px';

  const nav = document.querySelector('[aura-section-nav]');

  // Do not proceed if the page has no Section Nav. There is no link to mark.
  if (!nav) {
    return;
  }

  // Only items with a target are links. An item with no target renders as a
  // plain span and owns no section.
  const links = Array.from(nav.querySelectorAll('a.aura-countercool-section-nav__link'));

  /**
   * Pair every link with the element it points at, in page order.
   *
   * @param {HTMLAnchorElement[]} navLinks - The Section Nav links.
   * @returns {{ link: HTMLAnchorElement, section: HTMLElement }[]} Pairs whose section exists.
   */
  const pairLinksWithSections = (navLinks) => {
    const pairs = navLinks.map((link) => {
      // Read the attribute, because `link.href` expands to a full URL.
      const targetId = link.getAttribute('href').slice(1);

      return { link: link, section: document.getElementById(targetId) };
    });

    const found = pairs.filter((pair) => pair.section !== null);

    return found.sort((a, b) => {
      // The FOLLOWING bit is set when b comes after a, which is scroll order.
      const bFollowsA = a.section.compareDocumentPosition(b.section);

      return bFollowsA & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
  };

  const pairs = pairLinksWithSections(links);

  // Do not proceed if no link points at a section. Nothing would ever be marked.
  if (pairs.length === 0) {
    return;
  }

  const inBand = new Set();

  /**
   * Move `aria-current` to the link for the last section that reached the band.
   *
   * @returns {void}
   */
  const markCurrentLink = () => {
    // Keeping the last match in page order picks the section furthest down,
    // which is the one just scrolled into when two share the band.
    let current = null;

    pairs.forEach((pair) => {
      if (inBand.has(pair.section)) {
        current = pair.link;
      }
    });

    pairs.forEach((pair) => {
      if (pair.link === current) {
        pair.link.setAttribute('aria-current', 'true');
        return;
      }

      pair.link.removeAttribute('aria-current');
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          inBand.add(entry.target);
          return;
        }

        inBand.delete(entry.target);
      });

      markCurrentLink();
    },
    { rootMargin: OBSERVED_BAND },
  );

  pairs.forEach((pair) => observer.observe(pair.section));
})();
